import PropTypes from 'prop-types';
import { Calendar, MapPin, Trophy, Video } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './ActivityItem.module.css';

const activityIcons = {
  joined_event: Calendar,
  check_in: MapPin,
  achievement: Trophy,
  highlight: Video,
};

const activityTexts = {
  joined_event: (data) => `joined ${data.event}`,
  check_in: (data) => `checked in at ${data.venue}`,
  achievement: (data) => `unlocked "${data.achievement}"`,
  highlight: (data) => data.description,
};

/**
 * ActivityItem - Displays a single friend activity in the feed
 * 
 * @param {Object} activity - Activity data object
 * @param {function} onAction - Optional action button click handler
 * @param {Object} style - Additional inline styles
 */
export function ActivityItem({ activity, onAction, style }) {
  const IconComponent = activityIcons[activity.type] || Calendar;
  const activityText = activityTexts[activity.type] 
    ? activityTexts[activity.type](activity)
    : 'did something';

  return (
    <article className={styles.item} style={style}>
      {/* User Avatar */}
      <div className={styles.avatarContainer}>
        <div className={styles.avatar}>
          {activity.user.name.charAt(0).toUpperCase()}
        </div>
        <div className={styles.iconBadge}>
          <IconComponent size={10} strokeWidth={2.5} />
        </div>
      </div>

      {/* Activity Content */}
      <div className={styles.content}>
        <Stack direction="column" gap={0}>
          <div className={styles.mainText}>
            <Text size="sm" color="primary" weight="semibold" as="span">
              {activity.user.name}
            </Text>
            {' '}
            <Text size="sm" color="secondary" as="span">
              {activityText}
            </Text>
          </div>
          <Text size="xs" color="muted" className={styles.time}>
            {activity.time}
          </Text>
        </Stack>
      </div>

      {/* Optional Action Button */}
      {onAction && (
        <button 
          className={styles.actionButton}
          onClick={onAction}
          aria-label="View details"
        >
          View
        </button>
      )}
    </article>
  );
}

ActivityItem.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.oneOf(['joined_event', 'check_in', 'achievement', 'highlight']).isRequired,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    event: PropTypes.string,
    venue: PropTypes.string,
    achievement: PropTypes.string,
    description: PropTypes.string,
    time: PropTypes.string.isRequired,
  }).isRequired,
  onAction: PropTypes.func,
  style: PropTypes.object,
};

export default ActivityItem;
